import React, { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banknote, CirclePlay, Menu, ReceiptText, Star, X } from 'lucide-react';
import { collection, deleteDoc, doc, getDoc, getDocs, orderBy, query, runTransaction, serverTimestamp, where } from 'firebase/firestore';
import { db } from '../firebase';
import { checkWhatsAppNumber, normalizeBangladeshPhone } from '../utils/phone';
import './HomePage.css';

const STORAGE_KEYS = {
  admin: 'earneeds_admin_session',
  registrations: 'earneeds_local_registrations'
};

const REGISTRATION_SUGGESTIONS = {
  occupation: ['শিক্ষার্থী', 'গৃহিণী', 'চাকরিজীবী', 'ব্যবসায়ী', 'ফ্রিল্যান্সার', 'অন্যান্য']
};

function RegistrationSuggestionField({ name, label, value, onChange, suggestions, placeholder }) {
  const listId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const filteredSuggestions = (suggestions || []).filter((suggestion) =>
    suggestion.toLocaleLowerCase().includes(value.trim().toLocaleLowerCase())
  );

  return (
    <label className="suggestion-field">
      {label}
      <input
        name={name}
        value={value}
        onChange={onChange}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={listId}
      />
      {isOpen && filteredSuggestions.length > 0 && (
        <span className="suggestion-list" id={listId} role="listbox" aria-label={`${label} suggestion`}>
          {filteredSuggestions.slice(0, 12).map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              role="option"
              aria-selected={suggestion === value}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange({ target: { name, value: suggestion } });
                setIsOpen(false);
              }}
            >
              {suggestion}
            </button>
          ))}
        </span>
      )}
    </label>
  );
}

const readLocalRegistrations = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.registrations);
    if (!saved) return [];
    const data = JSON.parse(saved);
    return Array.isArray(data) ? data.map((row, index) => ({
      ...row,
      id: row.id || `local-${Date.parse(row.createdAt) || index}-${String(row.phone || '').replace(/\D/g, '')}`
    })) : [];
  } catch (error) {
    return [];
  }
};

const saveLocalRegistrations = (items) => {
  localStorage.setItem(STORAGE_KEYS.registrations, JSON.stringify(items));
};

const readDoneCache = () => {
  try {
    return JSON.parse(localStorage.getItem('earneeds_registration_done') || '{}');
  } catch (error) {
    return {};
  }
};

export const getAdminAccounts = async () => {
  const fallback = [{
    name: 'Tahsin',
    phone: '+8801889363706',
    password: 'tahsin@3706'
  }];

  try {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const userAdmins = usersSnapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter((item) => item.role === 'Admin' || item.password);

    const adminsSnapshot = await getDocs(collection(db, 'admins'));
    const adminDocs = adminsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    const combined = [...userAdmins, ...adminDocs];
    const seen = new Set();
    const uniqueAdmins = combined.filter((admin) => {
      const phone = normalizeBangladeshPhone(admin.phone || admin.phoneNumber);
      if (!phone || seen.has(phone)) return false;
      seen.add(phone);
      return true;
    });

    return uniqueAdmins.length ? uniqueAdmins : fallback;
  } catch (error) {
    return fallback;
  }
};

export const getAdminImgBBKey = async () => {
  try {
    const session = JSON.parse(localStorage.getItem(STORAGE_KEYS.admin) || '{}');
    const sessionPhone = normalizeBangladeshPhone(session.phone);
    if (!sessionPhone) return '';

    for (const collectionName of ['users', 'admins']) {
      const directSnapshot = await getDoc(doc(db, collectionName, sessionPhone)).catch(() => null);
      const directAdmin = directSnapshot?.exists() ? directSnapshot.data() : null;
      if (typeof directAdmin?.apiKey === 'string' && directAdmin.apiKey.trim()) {
        return directAdmin.apiKey.trim();
      }

      const snapshot = await getDocs(collection(db, collectionName)).catch(() => null);
      const admin = snapshot?.docs
        .map((item) => item.data())
        .find((item) => normalizeBangladeshPhone(item.phone || item.phoneNumber) === sessionPhone);
      if (typeof admin?.apiKey === 'string' && admin.apiKey.trim()) {
        return admin.apiKey.trim();
      }
    }

    return '';
  } catch (error) {
    return '';
  }
};

export const saveRegistration = async (payload) => {
  const normalizedPhone = normalizeBangladeshPhone(payload.phone);
  const localRecords = readLocalRegistrations();
  if (localRecords.some((row) => normalizeBangladeshPhone(row.phone) === normalizedPhone)) {
    return { ok: false, duplicate: true };
  }

  const record = {
    ...payload,
    phone: normalizedPhone,
    createdAt: new Date().toISOString(),
    done: false
  };

  try {
    const existing = await getDocs(query(
      collection(db, 'registrations'),
      where('phone', '==', normalizedPhone)
    ));
    if (!existing.empty) return { ok: false, duplicate: true };

    const registrationRef = doc(db, 'registrations', `phone-${normalizedPhone.replace(/\D/g, '')}`);
    const created = await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(registrationRef);
      if (snapshot.exists()) return false;
      transaction.set(registrationRef, {
      ...record,
      createdAt: serverTimestamp()
      });
      return true;
    });
    return created ? { ok: true, fallback: false } : { ok: false, duplicate: true };
  } catch (error) {
    const latestLocalRecords = readLocalRegistrations();
    if (latestLocalRecords.some((row) => normalizeBangladeshPhone(row.phone) === normalizedPhone)) {
      return { ok: false, duplicate: true };
    }
    saveLocalRegistrations([{ ...record, id: `local-${Date.now()}` }, ...latestLocalRecords]);
    return { ok: true, fallback: true };
  }
};

export const loadRegistrations = async () => {
  try {
    const snapshot = await getDocs(query(collection(db, 'registrations'), orderBy('createdAt', 'desc')));
    const doneCache = readDoneCache();
    const rows = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data(), done: doneCache[doc.id] ?? doc.data().done ?? false }));
    const localRows = readLocalRegistrations();
    return rows.length > 0 ? rows : localRows;
  } catch (error) {
    return readLocalRegistrations();
  }
};

export const setRegistrationDone = async (registration, done) => {
  if (!done) return false;

  if (registration.id && registration.id.startsWith('local-')) {
    const currentRows = readLocalRegistrations();
    const currentRegistration = currentRows.find((row) => row.id === registration.id);
    if (!currentRegistration) return false;
    if (currentRegistration.done) return true;
    const localRows = currentRows.map((row) => row.id === registration.id ? { ...row, done: true } : row);
    saveLocalRegistrations(localRows);
  } else {
    try {
      const registrationRef = doc(db, 'registrations', registration.id);
      const saved = await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(registrationRef);
        if (!snapshot.exists()) return false;
        if (snapshot.data().done) return true;
        transaction.update(registrationRef, { done: true });
        return true;
      });
      if (!saved) return false;
    } catch (error) {
      return false;
    }
  }

  const doneCache = readDoneCache();
  doneCache[registration.id] = true;
  localStorage.setItem('earneeds_registration_done', JSON.stringify(doneCache));
  return true;
};

export const deleteRegistration = async (registration) => {
  if (registration.id && !registration.id.startsWith('local-')) {
    try {
      await deleteDoc(doc(db, 'registrations', registration.id));
    } catch (error) {
      return false;
    }
  }

  const localRows = readLocalRegistrations().filter((row) => row.id !== registration.id);
  saveLocalRegistrations(localRows);

  const doneCache = readDoneCache();
  delete doneCache[registration.id];
  localStorage.setItem('earneeds_registration_done', JSON.stringify(doneCache));
  return true;
};

export function HomePage() {
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', occupation: '' });
  const [error, setError] = useState('');
  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value
    }));
  };

  const openForm = () => {
    setError('');
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setError('');
  };

  const handleRegister = async (event) => {
    event.preventDefault();

    const name = formData.name.trim();
    const phone = formData.phone.trim();

    if (!name) {
      setError('আপনার নাম লিখুন।');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const waCheck = await checkWhatsAppNumber(phone);
    if (!waCheck.hasWhatsApp) {
      setIsSubmitting(false);
      setWarningMessage('অনুগ্রহ করে সঠিক হোয়াটসঅ্যাপ নাম্বার দিন');
      setWarningOpen(true);
      return;
    }

    const optionalDetails = Object.fromEntries(
      ['occupation']
        .map((field) => [field, formData[field].trim()])
        .filter(([, value]) => value)
    );
    const result = await saveRegistration({ name, phone: waCheck.normalized || phone, ...optionalDetails });
    setIsSubmitting(false);

    if (result.duplicate) {
      setError('এই WhatsApp নম্বর দিয়ে আগে registration করা হয়েছে।');
      return;
    }

    if (result.ok) {
      setFormOpen(false);
      setFormData({ name: '', phone: '', occupation: '' });
      setSuccessOpen(true);
    }
  };

  return (
    <div className="earneeds-page">
      <header className="topbar">
        <div className="brand-wrap">
          <img className="brand-logo" src="/logo.png" alt="Earneeds logo" />
          <div className="brand-copy">
            <span className="brand-name">Earneeds</span>
            <span className="brand-tag">Learn • Work • Earn</span>
          </div>
        </div>

        <nav className="main-nav" aria-label="Main navigation">
          <button className="nav-link active" type="button">Home</button>
          <button className="nav-link" type="button">How It Works</button>
          <button className="nav-link" type="button">Earning Options</button>
          <button className="nav-link" type="button">Training</button>
          <button className="nav-link" type="button">About</button>
        </nav>

        <div className="top-actions">
          <button className="search-btn" type="button" aria-label="Search">⌕</button>
          <button className="login-btn" type="button" onClick={() => navigate('/admin-login')}>Login</button>
          <button className="join-btn" type="button" onClick={openForm}>Join Now</button>
        </div>

        <div className="mobile-menu-wrap">
          <button
            className="mobile-menu-toggle"
            type="button"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            {mobileMenuOpen ? <X size={25} /> : <Menu size={25} />}
          </button>
          {mobileMenuOpen && (
            <div className="mobile-menu-panel" id="mobile-menu">
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/admin-login');
                }}
              >
                Login
              </button>
            </div>
          )}
        </div>
      </header>

      <nav className="public-options-card" aria-label="কনটেন্ট পেইজ">
        <button type="button" className="public-option" onClick={() => navigate('/payment-proofs')}>
          <ReceiptText size={25} aria-hidden="true" />
          <span>পেমেন্ট প্রুফ</span>
        </button>
        <button type="button" className="public-option" onClick={() => navigate('/user-reviews')}>
          <Star size={25} aria-hidden="true" />
          <span>ইউজার রিভিউ</span>
        </button>
        <button type="button" className="public-option" onClick={() => navigate('/work-videos')}>
          <CirclePlay size={25} aria-hidden="true" />
          <span>কাজের ভিডিও</span>
        </button>
        <button type="button" className="public-option" onClick={() => navigate('/salary-list')}>
          <Banknote size={25} aria-hidden="true" />
          <span>সেলারি লিস্ট</span>
        </button>
      </nav>

      <main className="hero-section">
        <div className="hero-copy">
          <div className="hero-description-card">
            <p className="hero-text">
              আমাদের কোম্পানিতে আপনি একাধিক উপায়ে ইনকাম করতে পারবেন। কাজের সুযোগগুলো হলো:
            </p>
            <ul className="hero-work-list">
              <li>ভিডিও দেখে ইনকাম</li>
              <li>ফর্ম পূরণ করে ইনকাম-১৫০৳</li>
              <li>প্রোডাক্ট সেল করে ইনকাম-আনলিমিটেড</li>
              <li>ফটো ও ভিডিও এডিটিং করে ইনকাম-২০৳</li>
              <li>ক্যাপকাট দিয়ে টেমপ্লেট তৈরি করে ইনকাম</li>
              <li>নিজের টিম গড়ে তুলে ইনকাম (যাদের কাজ শেখাবেন, তাদের থেকে ২০% কমিশন পাবেন)</li>
              <li>ফিক্সড মাসিক স্যালারি নিয়ে কাজ করার সুযোগ</li>
            </ul>
            <p className="hero-text">
              সব কাজ আপনি একদম ফ্রিতে শিখতে পারবেন।{' '}
              প্রথম দিন থেকেই ৩০০-৫০০ টাকা ইনকাম শুরু করতে পারবেন, আর সময়ের সাথে সাথে ইনকাম আরও বাড়বে।{' '}
              সরকারি চাকরি হয়তো আমরা দিতে পারবো না, তবে এমন একটি ভালো কাজ দিতে পারবো যা দিয়ে আপনি সুন্দরভাবে নিজের খরচ চালাতে পারবেন।
            </p>
          </div>

          <form onSubmit={handleRegister} className="register-form hero-register-form">
            <label>
              আপনার নাম
              <input name="name" value={formData.name} onChange={handleChange} placeholder="আপনার পুরো নাম লিখুন" />
            </label>
            <label>
              WhatsApp নম্বর
                <input name="phone" type="tel" inputMode="tel" value={formData.phone} onChange={handleChange} placeholder="01XXXXXXXXX" required />
            </label>
            <RegistrationSuggestionField name="occupation" label="পেশা (ঐচ্ছিক)" value={formData.occupation} onChange={handleChange} suggestions={REGISTRATION_SUGGESTIONS.occupation} placeholder="আপনার পেশা" />
            {error && <div className="form-error">{error}</div>}
            <button className="submit-btn" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'জমা হচ্ছে...' : 'Apply →'}
            </button>
          </form>

          <div className="stats-row">
            <div className="stat-box">
              <div className="stat-icon">👥</div>
              <div className="stat-copy">
                <strong>50K+</strong>
                <span>Active Members</span>
              </div>
            </div>
            <div className="stat-box">
              <div className="stat-icon">✓</div>
              <div className="stat-copy">
                <strong>100%</strong>
                <span>Safe & Secure</span>
              </div>
            </div>
            <div className="stat-box">
              <div className="stat-icon">✦</div>
              <div className="stat-copy">
                <strong>Multiple</strong>
                <span>Earning Options</span>
              </div>
            </div>
          </div>
        </div>

        <div className="hero-visual-wrap">
          <div className="visual-panel">
            <div className="hero-image-card">
              <img src="/hero.jpeg" alt="Woman working on a laptop" />
            </div>
          </div>
        </div>
      </main>

      <div className="bottom-feature-grid">
        <div className="feature-box"><span className="feature-icon">▶</span><strong>ভিডিও দেখুন</strong><small>সারাজীবন কাজ, দ্রুত আয়</small></div>
        <div className="feature-box"><span className="feature-icon">▣</span><strong>ফর্ম পূরণ</strong><small>প্রতি ফর্ম ৳ 150</small></div>
        <div className="feature-box"><span className="feature-icon">🛒</span><strong>পণ্য বিক্রি</strong><small>অনলাইনে কাজ, আয়</small></div>
        <div className="feature-box"><span className="feature-icon">✎</span><strong>ফটো এডিট</strong><small>প্রতি এডিট ৳ 20</small></div>
        <div className="feature-box"><span className="feature-icon">✦</span><strong>CapCut টেমপ্লেট</strong><small>নির্দিষ্ট শৈলী কাজ</small></div>
        <div className="feature-box"><span className="feature-icon">👥</span><strong>নির্দিষ্ট দল</strong><small>কমিশন ও আয়</small></div>
      </div>

      {formOpen && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="registration-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-head">
              <h3>Register</h3>
              <button type="button" className="close-btn" onClick={closeForm}>×</button>
            </div>
            <form onSubmit={handleRegister} className="register-form">
              <label>
                Name
                <input name="name" value={formData.name} onChange={handleChange} placeholder="Enter your full name" />
              </label>
              <label>
                WhatsApp Number
                <input name="phone" type="tel" inputMode="tel" value={formData.phone} onChange={handleChange} placeholder="01XXXXXXXXX" required />
              </label>
              <RegistrationSuggestionField name="occupation" label="পেশা (ঐচ্ছিক)" value={formData.occupation} onChange={handleChange} suggestions={REGISTRATION_SUGGESTIONS.occupation} placeholder="আপনার পেশা" />
              {error && <div className="form-error">{error}</div>}
              <button className="submit-btn" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Register Now'}
              </button>
            </form>
          </div>
        </div>
      )}

      {warningOpen && (
        <div className="modal-overlay" onClick={() => setWarningOpen(false)}>
          <div className="registration-modal warning-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-head">
              <h3>সতর্কবার্তা</h3>
              <button type="button" className="close-btn" onClick={() => setWarningOpen(false)}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <p style={{ margin: 0, fontSize: '1.05rem', lineHeight: '1.6', color: '#412d28' }}>
                {warningMessage}
              </p>
              <button type="button" className="submit-btn" onClick={() => setWarningOpen(false)}>
                ঠিক আছে
              </button>
            </div>
          </div>
        </div>
      )}

      {successOpen && (
        <div className="success-overlay" onClick={() => setSuccessOpen(false)}>
          <div className="success-card" onClick={(event) => event.stopPropagation()}>
            <h3>Congratulations</h3>
            <p>
              আপনার রেজিস্ট্রেশন সফল হয়েছে। কিছুক্ষণের মধ্যেই কোম্পানির স্যার/মেম আপনার সাথে
              হোয়াটসাপে যোগাযোগ করবেন। অবশ্যই তার SMS এর রিপ্লাই করবেন। ধন্যবাদ।
            </p>
            <button type="button" className="ok-btn" onClick={() => setSuccessOpen(false)}>Okay</button>
          </div>
        </div>
      )}

    </div>
  );
}
